/**
 * Карточка процесса (дровер и /processes/[id]) по макету «Карточка процесса» (16.10.2026) —
 * чистая логика раскладки: свёртка маршрута, итог под шапкой и что из действий идёт в футер, а
 * что в меню «⋯». Права и доступность по-прежнему считает ProcessActions — здесь только раскладка.
 */
import type { EdoDecision, EdoStatus, RequestCard } from '../../lib/types'
import type { RouteStep } from './registry'

// ── Маршрут ──────────────────────────────────────────────────────────────────

/**
 * Шаги, которые были или идут (done/current/rejected/skipped), — полностью; будущие требуемые
 * (pending) — строкой; «не потребовались» (na) — в одну раскрывающуюся строку под маршрутом
 * (у подбора таких бывает пять из шести).
 */
export function splitRoute(route: RouteStep[]): { shown: { step: RouteStep; compact: boolean }[]; na: RouteStep[] } {
  return {
    shown: route.filter((r) => r.state !== 'na').map((step) => ({ step, compact: step.state === 'pending' })),
    na: route.filter((r) => r.state === 'na'),
  }
}

/** Место в маршруте одной линией: шаг или свёрнутый промежуток «+N» (несколько шагов подряд). */
export type RouteSlot = { kind: 'step'; step: RouteStep } | { kind: 'gap'; steps: RouteStep[] }

/**
 * Маршрут одной линией на `max` мест (главная, RouteStepper): согласующих у документа бывает
 * десяток, и все шаги в ширину колонки не влезают (владелец, 02.11.2026). Всегда видны первый,
 * текущий и последний шаги, вокруг текущего — сколько поместится; остальное — промежутками «+N»,
 * каждый занимает одно место. Промежуток из одного шага не делаем — сам шаг места занимает столько же.
 * «Не требуется» (na) в линию не идут. Меньше 5 мест не бывает: первый, текущий, последний и два промежутка.
 */
export function windowRoute(route: RouteStep[], max: number): RouteSlot[] {
  const steps = route.filter((r) => r.state !== 'na')
  const n = steps.length
  const cap = Math.max(5, Math.floor(max))
  if (n <= cap) return steps.map((step) => ({ kind: 'step', step }))
  // Текущий шаг; нет его (закрыт, отклонён) — последний, до которого документ дошёл.
  let cur = steps.findIndex((r) => r.state === 'current')
  if (cur < 0) cur = steps.map((r) => r.state !== 'pending').lastIndexOf(true)
  if (cur < 0) cur = 0
  const keep = new Set([0, cur, n - 1])
  // мест = видимые шаги + промежутки (промежуток начинается там, где перед скрытым шагом виден шаг)
  const slots = (k: Set<number>) => {
    let s = 0
    for (let i = 0; i < n; i++) if (k.has(i) || k.has(i - 1)) s++
    return s
  }
  for (let d = 1; d < n; d++) {
    for (const i of [cur + d, cur - d]) {
      if (i <= 0 || i >= n - 1 || keep.has(i)) continue
      if (slots(new Set(keep).add(i)) <= cap) keep.add(i)
    }
  }
  // одиночный промежуток → сам шаг (мест столько же)
  for (let i = 1; i < n - 1; i++) if (!keep.has(i) && keep.has(i - 1) && keep.has(i + 1)) keep.add(i)
  const out: RouteSlot[] = []
  for (let i = 0; i < n; i++) {
    const last = out[out.length - 1]
    if (keep.has(i)) out.push({ kind: 'step', step: steps[i] })
    else if (last?.kind === 'gap') last.steps.push(steps[i])
    else out.push({ kind: 'gap', steps: [steps[i]] })
  }
  return out
}

/**
 * Открытый групповой шаг, который некому взять. Признак — из данных маршрута: карточка отдаёт
 * кандидатов текущего группового шага (`withCandidates` на бэке), пустой список = никого.
 * Текст аудита («…без исполнителя…» в событии `route`) не разбираем.
 */
export const noExecutorStep = (route: RouteStep[]): RouteStep | undefined =>
  route.find((r) => r.state === 'current' && !r.person && !!r.group && r.candidates?.length === 0)

// ── Итог под шапкой ─────────────────────────────────────────────────────────────

const TERMINAL: EdoStatus[] = ['APPROVED', 'POSTED', 'CLOSED', 'REGISTERED', 'AWAITING_TERMINATION_POSTING', 'REJECTED', 'POST_FAILED', 'CANCELLED']
export const isTerminal = (s?: EdoStatus) => !!s && TERMINAL.includes(s)

/** Отзыв: последнее событие `cancelled` — кто (автор или нет), когда и причина. */
export function cancelInfo(card: Pick<RequestCard, 'auditEvents' | 'initiator' | 'submittedBy'>) {
  const ev = [...(card.auditEvents ?? [])].reverse().find((e) => e.event === 'cancelled')
  if (!ev) return undefined
  const id = ev.actor?.id
  return {
    byAuthor: id ? id === card.initiator?.id || id === card.submittedBy?.id : null,
    actor: ev.actor?.fullName,
    reason: ev.comment ?? undefined,
    at: ev.createdAt,
  }
}

/** Срок текущего шага истёк: эскалация или напоминание SLA, записанные уже при открытой задаче. */
export function overdueAt(card: Pick<RequestCard, 'tasks' | 'auditEvents'>): string | undefined {
  const open = card.tasks?.find((tk) => String(tk.status) === 'OPEN')
  if (!open?.createdAt) return undefined
  return [...(card.auditEvents ?? [])]
    .reverse()
    .find((e) => (e.event === 'escalation' || e.event === 'sla_reminder') && e.createdAt >= open.createdAt!)?.createdAt
}

// ── Действия: футер и меню «⋯» ───────────────────────────────────────────────────

export type ActionKey =
  | `decide:${EdoDecision}`
  | 'claim' | 'unclaim' | 'assign' | 'close' | 'exit' | 'accrue' | 'remind'
  | 'print' | 'addApprover' | 'copyLink' | 'openPage' | 'cancel'

/** Что доступно — те же флаги, что ProcessActions считал и раньше. */
export interface ActionAvailability {
  decisions: EdoDecision[]
  claim: boolean
  unclaim: boolean
  assign: boolean
  close: boolean
  exit: boolean
  accrue: boolean
  remind: boolean
  print: boolean
  addApprover: boolean
  copyLink: boolean
  openPage: boolean
  cancel: boolean
}

/**
 * Главное действие по ситуации: групповой шаг — «Взять в работу»; согласующий — все решения шага
 * (доработку в меню «⋯» не находили — 05.11.2026); шаг закрытия подбора, exit-интервью,
 * начисление; инициатор — «Напомнить»; иначе (терминальный документ) — «Лист согласования».
 * Всё остальное доступное — в меню, порядок как в макете; отзыв — последним.
 */
export function planActions(a: ActionAvailability): { primary: ActionKey[]; menu: ActionKey[] } {
  const decide = a.decisions.map((d) => `decide:${d}` as const)
  const primary: ActionKey[] = a.claim
    ? ['claim']
    : a.close
      ? ['close']
      : a.exit
        ? ['exit']
        : decide.length
          ? decide
          : a.accrue
            ? ['accrue']
            : a.remind
              ? ['remind']
              : a.print
                ? ['print']
                : []
  const all: ActionKey[] = [
    ...(a.claim ? (['claim'] as const) : []),
    ...(a.close ? (['close'] as const) : []),
    ...(a.exit ? (['exit'] as const) : []),
    ...decide,
    ...(a.unclaim ? (['unclaim'] as const) : []),
    ...(a.accrue ? (['accrue'] as const) : []),
    ...(a.remind ? (['remind'] as const) : []),
    ...(a.print ? (['print'] as const) : []),
    ...(a.copyLink ? (['copyLink'] as const) : []),
    ...(a.openPage ? (['openPage'] as const) : []),
    ...(a.addApprover ? (['addApprover'] as const) : []),
    ...(a.assign ? (['assign'] as const) : []),
    ...(a.cancel ? (['cancel'] as const) : []),
  ]
  return { primary, menu: all.filter((k) => !primary.includes(k)) }
}

/**
 * Последнее уточнение агентства в истории заявки. Нужен куратору: отвечать, не видя вопроса,
 * нельзя, а в истории он лежит среди решений согласующих и маршрутных событий.
 */
export function lastAgencyQuestion(
  card: Pick<RequestCard, 'auditEvents'>,
): { text: string; author: string | null; at: string } | undefined {
  const ev = [...(card.auditEvents ?? [])]
    .filter((e) => e.event === 'agency_question' && !!e.comment?.trim())
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .at(-1)
  return ev ? { text: ev.comment!.trim(), author: ev.actor?.fullName ?? null, at: ev.createdAt } : undefined
}
